/**
 * Slice 11 — "vitrina honesta". Lo que la tienda afirma (sellos, políticas,
 * contacto) sale de `bootstrap.storefront.trust`, que arma la API desde la
 * configuración real del negocio. Nunca hay textos fijos de respaldo: si el
 * bootstrap no trae `trust` (API vieja), se derivan solo los sellos que la
 * propia config del checkout confirma; si no hay bootstrap, no se muestra nada.
 */

import { buildStorePathFor } from "./product-path";

export type TrustBadgeKind =
  | "CASH_ON_DELIVERY"
  | "ENZONA"
  | "STORE_PICKUP"
  | "HOME_DELIVERY";

export type TrustPolicyLink = { slug: string; title: string };

export type StorefrontTrust = {
  topBarMessage: string | null;
  footerTagline: string | null;
  autoBadges: TrustBadgeKind[];
  customBadges: Array<{ label: string; icon: string }>;
  policyLinks: TrustPolicyLink[];
  contact: { email: string | null; phone: string | null };
};

export const TRUST_BADGE_LABELS: Record<TrustBadgeKind, string> = {
  CASH_ON_DELIVERY: "Pago contra entrega",
  ENZONA: "Pago con EnZona",
  STORE_PICKUP: "Recogida en tienda",
  HOME_DELIVERY: "Envío a domicilio",
};

const BADGE_KINDS = Object.keys(TRUST_BADGE_LABELS) as TrustBadgeKind[];

export const EMPTY_TRUST: StorefrontTrust = {
  topBarMessage: null,
  footerTagline: null,
  autoBadges: [],
  customBadges: [],
  policyLinks: [],
  contact: { email: null, phone: null },
};

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v.length > 0 ? v : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Sellos que el checkout confirma por sí solo (fallback sin `trust`). EnZona nunca. */
function badgesFromCheckoutConfig(sf: Record<string, unknown>): TrustBadgeKind[] {
  const badges: TrustBadgeKind[] = [];
  if (sf.paymentMode === "COD_ONLY" || sf.paymentMode === "BOTH") {
    badges.push("CASH_ON_DELIVERY");
  }
  if (sf.allowsPickup === true) badges.push("STORE_PICKUP");
  if (sf.allowsDelivery === true) badges.push("HOME_DELIVERY");
  return badges;
}

/** Normaliza `bootstrap.storefront` → StorefrontTrust. Nunca lanza. */
export function parseStorefrontTrust(storefront: unknown): StorefrontTrust {
  if (!isRecord(storefront)) return EMPTY_TRUST;

  const raw = storefront.trust;
  if (!isRecord(raw)) {
    return { ...EMPTY_TRUST, autoBadges: badgesFromCheckoutConfig(storefront) };
  }

  const autoBadges = Array.isArray(raw.autoBadges)
    ? BADGE_KINDS.filter((kind) => (raw.autoBadges as unknown[]).includes(kind))
    : [];

  const customBadges = Array.isArray(raw.customBadges)
    ? raw.customBadges.flatMap((b) => {
        if (!isRecord(b)) return [];
        const label = text(b.label);
        const icon = text(b.icon);
        return label && icon ? [{ label, icon }] : [];
      })
    : [];

  const policyLinks = Array.isArray(raw.policyLinks)
    ? raw.policyLinks.flatMap((p) => {
        if (!isRecord(p)) return [];
        const slug = text(p.slug);
        const title = text(p.title);
        return slug && title ? [{ slug, title }] : [];
      })
    : [];

  const contact = isRecord(raw.contact) ? raw.contact : {};

  return {
    topBarMessage: text(raw.topBarMessage),
    footerTagline: text(raw.footerTagline),
    autoBadges,
    customBadges,
    policyLinks,
    contact: { email: text(contact.email), phone: text(contact.phone) },
  };
}

export function hasContact(trust: StorefrontTrust): boolean {
  return !!(trust.contact.email || trust.contact.phone);
}

export function findPolicy(
  trust: StorefrontTrust,
  slug: string,
): TrustPolicyLink | null {
  return trust.policyLinks.find((p) => p.slug === slug) ?? null;
}

/**
 * Destinos de las rutas que ya no tienen página propia (plantillas falsas
 * retiradas en el Slice 11). Relativos a la tienda; el caller les pone el
 * prefijo con buildStorePath y conserva el query string.
 */
export const RETIRED_ROUTE_TARGETS = {
  "/legal/terms": "/pages/terms",
  "/legal/privacy": "",
  "/help": "",
} as const;

export type RetiredRoute = keyof typeof RETIRED_ROUTE_TARGETS;

/** `suffix` + query string, sin duplicar el `?`. */
export function withSearch(path: string, search: string | null | undefined): string {
  const q = typeof search === "string" ? search.replace(/^\?/, "") : "";
  return q ? `${path}?${q}` : path;
}

/** Destino absoluto de una ruta retirada, con prefijo de tienda y query string. */
export function retiredRouteTarget(
  orgSlug: string | null | undefined,
  route: RetiredRoute,
  search?: string | null,
): string {
  return withSearch(buildStorePathFor(orgSlug, RETIRED_ROUTE_TARGETS[route]), search);
}
